import {
  Close,
  Drafts,
  ErrorOutline,
  Info,
  Warning,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
  colors,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { Alert } from 'switch-shared/modules/crud/alert/alert.entity';
import { AlertSeverity } from 'switch-shared/modules/crud/alert/alert.enum';

interface Props {
  open: boolean;
  onClose: () => void;
  alerts: Alert[];
  title?: string;
  isLoading?: boolean;
}

const getListItemIcon = (severity: AlertSeverity) => {
  switch (severity) {
    case AlertSeverity.Info:
      return (
        <ListItemIcon>
          <Avatar sx={{ backgroundColor: colors.green[900] }}>
            <Info />
          </Avatar>
        </ListItemIcon>
      );
    case AlertSeverity.Error:
    case AlertSeverity.Critical:
      return (
        <ListItemIcon>
          <Avatar sx={{ backgroundColor: colors.red[900] }}>
            <ErrorOutline />
          </Avatar>
        </ListItemIcon>
      );
    case AlertSeverity.Warning:
      return (
        <ListItemIcon>
          <Avatar sx={{ backgroundColor: colors.yellow[900] }}>
            <Warning />
          </Avatar>
        </ListItemIcon>
      );
    default:
      return (
        <ListItemIcon>
          <Avatar>
            <Drafts />
          </Avatar>
        </ListItemIcon>
      );
  }
};

const AlertsModal = ({
  open,
  onClose,
  alerts,
  title = 'Active Alerts',
  isLoading = false,
}: Props) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: theme.tokens.typography.fontSize.lg,
          fontWeight: 'bold',
        }}
      >
        {title}
        <IconButton
          onClick={onClose}
          size='small'
          sx={{
            color: theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1 }}>
        {isLoading ? (
          <List>
            {[1, 2, 3].map((index) => (
              <ListItem key={index}>
                <ListItemAvatar>
                  <Skeleton variant='circular' width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant='text' width='80%' />}
                  secondary={<Skeleton variant='text' width='60%' />}
                />
              </ListItem>
            ))}
          </List>
        ) : alerts.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant='body1'>
              No alerts currently. Add meter rules to be notified of unusual
              usage
            </Typography>
          </Box>
        ) : (
          <List>
            {alerts.map((alert) => (
              <ListItem key={alert._id}>
                {getListItemIcon(alert.severity)}
                <ListItemText
                  primary={alert.cause.description}
                  secondary={`Last triggered at ${dayjs(
                    alert.lastTriggeredAt
                  ).format('HH:mm, D MMMM YYYY')}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AlertsModal;
